/**
 * Toast messages for the tax-closing feature (extraction + dashboard).
 * Centralized so the wording stays consistent across the screen and so we
 * don't have inline strings sprinkled across components/hooks.
 *
 * Pattern: each entry is a function that returns a `Parameters<typeof toast>[0]`
 * (title + description + optional variant). Callers do `toast(MESSAGES.foo(...))`.
 */

type ToastVariant = "default" | "destructive";

export interface ToastPayload {
  title: string;
  description: string;
  variant?: ToastVariant;
}

export const TAX_CLOSING_TOASTS = {
  generateSuccess: (storeCount: number, missingCount: number): ToastPayload => ({
    title: "Extração concluída",
    description:
      missingCount > 0
        ? `${storeCount} loja(s) processada(s) — ${missingCount} sem dados OFM`
        : `${storeCount} loja(s) processada(s)`,
  }),
  generateError: (errorTitle: string, errorDescription: string): ToastPayload => ({
    title: errorTitle,
    description: errorDescription,
    variant: "destructive",
  }),
  reprocessRequested: (storeCount: number, schedulerId: string): ToastPayload => ({
    title: "Reprocessamento OFM solicitado",
    description: `${storeCount} loja(s). Scheduler: ${schedulerId}`,
  }),
  reprocessError: (description: string): ToastPayload => ({
    title: "Falha ao solicitar reprocessamento",
    description,
    variant: "destructive",
  }),
  reprocessAlreadyPending: (): ToastPayload => ({
    title: "Reprocessamento já em andamento",
    description:
      "Já existe um reprocessamento OFM em andamento para este período. Aguarde a conclusão.",
  }),
  excelExportError: (description: string): ToastPayload => ({
    title: "Falha ao exportar",
    description,
    variant: "destructive",
  }),
  excelGenerating: (): ToastPayload => ({
    title: "Excel em geração",
    description:
      "O arquivo ainda está sendo gerado. Tente novamente em instantes.",
  }),
  excelNotAvailable: (): ToastPayload => ({
    title: "Falha ao exportar",
    description: "Excel não disponível para este job",
    variant: "destructive",
  }),
  excelExportGenericError: (): ToastPayload => ({
    title: "Falha ao exportar",
    description: "Erro ao exportar Excel",
    variant: "destructive",
  }),
  excelExportTimeout: (): ToastPayload => ({
    title: "Falha ao exportar",
    description:
      "A geração está demorando mais que o esperado. Tente novamente em instantes.",
    variant: "destructive",
  }),
  excelEmptyResponse: (): ToastPayload => ({
    title: "Falha ao exportar",
    description: "O servidor não devolveu o arquivo. Tente novamente.",
    variant: "destructive",
  }),
  varRefreshSuccess: (
    totalRows: number,
    rowsWithoutMatch: number,
    rowsWithDivergence: number,
  ): ToastPayload => ({
    title: "Aba VAR atualizada",
    description:
      totalRows > 0
        ? `${totalRows} linhas processadas — ${rowsWithoutMatch} sem match no GFT, ${rowsWithDivergence} com divergência`
        : "Sem dados VAR para o período",
  }),
  varRefreshError: (description: string): ToastPayload => ({
    title: "Falha ao atualizar VAR",
    description,
    variant: "destructive",
  }),
} as const;
